import { useParams, Link } from 'react-router-dom';
import { FadeUp, useServiceImages } from '../hooks';
import ContactForm from '../components/ContactForm';
import '../styles.css';

const services = {
  'functional-art': {
    num: '02', title: 'Custom Installations & Functional Art',
    tagline: 'Crafted at the intersection of function and design.',
    lead: "We build things that are both structurally sound and visually deliberate. Staircases, built-in cabinetry, architectural millwork, and one-of-a-kind custom elements — designed for the space and built to outlast it.",
    body: "Most contractors build what's spec'd. We think about what the space needs and why. Functional art starts with a conversation about how you actually use a room — then we design and build something that serves that use and elevates the space at the same time.",
    steps: [
      { title: 'Site Assessment', desc: 'We walk the space with you. Existing conditions, structural constraints, lighting, traffic flow — everything that shapes what\'s possible.' },
      { title: 'Design & Mockup', desc: 'Detailed drawings so there is a clear understanding of the agreed direction' },
      { title: 'Material Selection', desc: 'Hardwoods, metals, concrete, glass — we source materials that match the intended look and structural requirement.' },
      { title: 'Fabrication & Install', desc: 'We build and install in-house where possible. Specialty finishes go to vetted local shops.' },
      { title: 'Final Fit & Finish', desc: 'Every detail is dialed before we consider the job done. Hardware, reveals, transitions, paint — all to specified details' },
    ],
    scope: { left: ['Built-in cabinetry and shelving', 'Statement staircases and railings', 'Architectural millwork and trim', 'Custom mantels and surrounds', 'Feature walls and paneling'], right: ['Kitchen and bath custom cabinetry', 'Built-in desks and workstations', 'Wine storage and display', 'Custom doors and hardware', 'Commercial display and fixture work'] },
  },
  'repairs': {
    num: '01', title: 'Repairs & Renovations',
    tagline: 'Restoring what exists, elevating what comes next.',
    lead: 'Full-scope remodels and precision repairs for residential and commercial properties — restoring what exists, elevating what comes next.',
    body: 'Renovation work exposes what\'s actually going on inside a building. We document conditions as we go, flag anything unexpected before it becomes a problem, and keep you informed at every stage.',
    steps: [
      { title: 'Assessment & Documentation', desc: 'We inspect existing conditions thoroughly before quoting. Hidden problems get surfaced in the estimate, not on the invoice.' },
      { title: 'Scope & Schedule', desc: 'Clear line-item scope and a realistic schedule before any work begins. Changes are documented and approved in writing.' },
      { title: 'Demolition & Prep', desc: 'We demo carefully, protect adjacent finishes, and dispose properly. The job site stays organized throughout.' },
      { title: 'Rough Work', desc: 'Framing, plumbing, electrical, mechanical — all coordinated and inspected before anything gets closed up.' },
      { title: 'Finish Work', desc: 'Tile, flooring, paint, trim, fixtures — sequenced correctly so finishes don\'t get damaged by subsequent trades.' },
    ],
    scope: { left: ['Full kitchen and bath remodels', 'Room additions and conversions', 'Window and door replacement', 'Flooring installation and refinishing', 'Interior and exterior painting'], right: ['Deck and porch repairs', 'Plumbing and fixture updates', 'Electrical upgrades', 'Commercial tenant improvements', 'Roofing repairs and replacement'] },
  },
  'structural': {
    num: '03', title: 'Structural Work',
    tagline: 'The structure beneath everything else.',
    lead: 'Foundations. Framing. Load-bearing systems. The structure beneath everything else — assessed, repaired, and built to last.',
    body: "Structural problems don't get better on their own. We assess what's actually failing, document it clearly, and repair it properly — whether that means sister joists, epoxy injection, full mudsill replacement, or seismic retrofit.",
    steps: [
      { title: 'Structural Assessment', desc: 'We inspect foundations, framing, load paths, and connections. Complex situations involve a licensed structural engineer.' },
      { title: 'Engineering & Permitting', desc: "If necessary, we prepare permit documentation and coordinate with engineers. We don't start structural work without approvals in hand" },
      { title: 'Shoring & Staging', desc: 'Proper temporary support is installed before any load-bearing elements are disturbed. This step doesn\'t get skipped.' },
      { title: 'Repair & Replacement', desc: 'Deteriorated members are replaced, not sistered over. New work is connected to existing structure properly.' },
      { title: 'Inspection & Close-Out', desc: 'All structural work is inspected before it\'s covered. We provide documentation of what was done and why.' },
    ],
    scope: { left: ['Foundation crack repair and waterproofing', 'Mudsill and cripple wall replacement', 'Seismic retrofit', 'Post and beam repair', 'Joist and rafter repair'], right: ['Dry rot remediation', 'Load-bearing wall removal (with engineering)', 'Foundation bolting', 'Retaining wall repair', 'Termite damage repair'] },
  },
};

const serviceOrder = ['repairs', 'functional-art', 'structural'];

function imgSrc(img) {
  return `${process.env.PUBLIC_URL}${img.src}`;
}

function imgSrcSet(img) {
  if (!img?.srcset) return undefined;
  return img.srcset.split(', ')
    .map(part => {
      const i = part.lastIndexOf(' ');
      return `${process.env.PUBLIC_URL}${part.slice(0, i)} ${part.slice(i + 1)}`;
    })
    .join(', ');
}

export default function ServicePage() {
  const { slug } = useParams();
  const svc = services[slug] || services.repairs;
  const images = useServiceImages(slug);

  return (
    <>
      <style>{svcStyles}</style>

      {/* HERO */}
      <div className="svc-hero">
        <div className="svc-hero-bg">
          {images[0] && <img src={imgSrc(images[0])} srcSet={imgSrcSet(images[0])} sizes="100vw" alt={svc.title} />}
          <div className="svc-hero-pattern" />
        </div>
        <div className="svc-hero-content">
          <div className="svc-num">Service {svc.num}</div>
          <h1>{svc.title}</h1>
        </div>
      </div>

      {/* SUB NAV */}
      <div className="svc-subnav">
        {serviceOrder.map((s) => {
          const d = services[s];
          return (
          <Link key={s} to={`/services/${s}`} className={s === slug ? 'active' : ''}>
            {d.num} — {d.title.split('&')[0].trim()}
          </Link>
          );
        })}
      </div>

      {/* OVERVIEW + PROCESS */}
      <div className="svc-overview">
        <FadeUp>
          <div>
            <div className="section-eyebrow">Overview</div>
            <h2 className="section-title">{svc.tagline}</h2>
            <div className="section-rule" />
            <p style={{ fontSize: '1.05rem', fontWeight: 300, lineHeight: 1.8, color: 'var(--mid)', marginBottom: '1rem' }}>{svc.lead}</p>
            <p style={{ fontSize: '0.9rem', fontWeight: 300, lineHeight: 1.85, color: 'var(--mid)' }}>{svc.body}</p>
          </div>
        </FadeUp>
        <FadeUp delay={150}>
          <div>
            <div className="section-eyebrow">Our Process</div>
            {svc.steps.map((step, i) => (
              <div key={i} className="process-step">
                <div className="step-num">0{i + 1}</div>
                <div>
                  <h4 style={{ fontFamily: 'Barlow, sans-serif', fontSize: '0.9rem', fontWeight: 500, color: 'var(--dark)', marginBottom: '0.4rem' }}>{step.title}</h4>
                  <p style={{ fontSize: '0.85rem', fontWeight: 300, lineHeight: 1.7, color: 'var(--mid)' }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </FadeUp>
      </div>

      {/* SCOPE */}
      <div className="svc-scope">
        <FadeUp>
          <div>
            <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.6rem', fontWeight: 300, color: 'var(--dark)', marginBottom: '1.5rem' }}>What's Included</h3>
            <ul className="scope-list">{svc.scope.left.map((item, i) => <li key={i}>{item}</li>)}</ul>
          </div>
        </FadeUp>
        <FadeUp delay={100}>
          <div>
            <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.6rem', fontWeight: 300, color: 'var(--dark)', marginBottom: '1.5rem' }}>Also In This Category</h3>
            <ul className="scope-list">{svc.scope.right.map((item, i) => <li key={i}>{item}</li>)}</ul>
          </div>
        </FadeUp>
      </div>

      {/* IMAGES */}
      <div className="svc-images">
        <FadeUp>
          <div className="section-eyebrow">Project Work</div>
          <h2 className="section-title" style={{ marginBottom: '2rem' }}>From the Field</h2>
        </FadeUp>
        <FadeUp delay={100}>
          <div className="svc-img-grid">
            {(images.length > 0 ? images : Array(5).fill(null)).map((src, i) => (
              <div key={i} className={`svc-img-wrap${i === 0 ? ' tall' : ''}`}>
                {src
                  ? <img src={imgSrc(src)} srcSet={imgSrcSet(src)} sizes="(max-width: 900px) 100vw, 25vw" alt={`${svc.title} ${i + 1}`} />
                  : <div className="svc-img-ph">Image {i + 1}</div>
                }
              </div>
            ))}
          </div>
        </FadeUp>
      </div>

      {/* CTA */}
      <div className="svc-cta">
        <FadeUp>
          <div>
            <div className="section-eyebrow" style={{ color: 'var(--aqua-light)' }}>Get Started</div>
            <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '2.5rem', fontWeight: 300, color: 'white', lineHeight: 1.2, marginBottom: '1rem' }}>Start a Project</h2>
            <div className="section-rule" style={{ background: 'var(--gold)' }} />
            <p style={{ fontSize: '0.9rem', fontWeight: 300, lineHeight: 1.8, color: 'rgba(255,255,255,0.55)' }}>Tell us about your project. We'll respond within one business day.</p>
          </div>
        </FadeUp>
        <FadeUp delay={150}>
          <ContactForm defaultProjectType={svc.title} />
        </FadeUp>
      </div>
    </>
  );
}

const svcStyles = `
.svc-hero { position:relative; height:60vh; min-height:400px; background:var(--dark); overflow:hidden; display:flex; align-items:flex-end; padding-top:72px; }
.svc-hero-bg { position:absolute; inset:0; background:linear-gradient(135deg,var(--aqua-dark),var(--dark)); }
.svc-hero-bg img { width:100%; height:100%; object-fit:cover; filter:brightness(.45); position:absolute; inset:0; }
.svc-hero-pattern { position:absolute; inset:0; background-image:repeating-linear-gradient(45deg,rgba(141,189,189,.03) 0,rgba(141,189,189,.03) 1px,transparent 1px,transparent 40px); }
.svc-hero-content { position:relative; padding:0 5rem 3.5rem; z-index:2; }
.svc-num { font-family:'Barlow Condensed',sans-serif; font-size:.65rem; font-weight:700; letter-spacing:.28em; text-transform:uppercase; color:var(--gold); margin-bottom:.75rem; }
.svc-hero-content h1 { font-family:'Cormorant Garamond',serif; font-size:3.5rem; font-weight:300; color:white; line-height:1.1; max-width:680px; }
.svc-subnav { background:var(--off-white); border-bottom:1px solid rgba(141,189,189,.2); display:flex; overflow-x:auto; }
.svc-subnav a { font-family:'Barlow Condensed',sans-serif; font-size:.72rem; font-weight:600; letter-spacing:.15em; text-transform:uppercase; color:var(--mid); text-decoration:none; padding:1.1rem 2rem; border-right:1px solid rgba(141,189,189,.2); white-space:nowrap; transition:color .2s; position:relative; }
.svc-subnav a::after { content:''; position:absolute; bottom:0; left:0; right:0; height:2px; background:var(--gold); transform:scaleX(0); transition:transform .25s; }
.svc-subnav a:hover, .svc-subnav a.active { color:var(--aqua-dark); }
.svc-subnav a.active::after, .svc-subnav a:hover::after { transform:scaleX(1); }
.svc-overview { display:grid; grid-template-columns:1fr 1.5fr; gap:6rem; padding:5rem; border-bottom:1px solid rgba(141,189,189,.15); }
.process-step { display:flex; gap:1.5rem; padding:1.5rem 0; border-bottom:1px solid rgba(141,189,189,.2); }
.process-step:last-child { border-bottom:none; }
.step-num { font-family:'Barlow Condensed',sans-serif; font-size:1.8rem; font-weight:300; color:var(--aqua-light); line-height:1; width:48px; flex-shrink:0; }
.svc-images { padding:4rem 5rem; background:var(--off-white); }
.svc-img-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:.875rem; }
.svc-img-grid .tall { grid-row:span 2; }
.svc-img-wrap { overflow:hidden; background:var(--aqua-dark); }
.svc-img-wrap img { width:100%; height:100%; object-fit:cover; display:block; min-height:180px; transition:transform .45s, filter .3s; filter:brightness(.88); }
.svc-img-wrap:hover img { transform:scale(1.05); filter:brightness(.72); }
.svc-img-ph { min-height:180px; background:linear-gradient(135deg,rgba(55,100,123,.35),rgba(141,189,189,.35)); display:flex; align-items:center; justify-content:center; font-family:'Barlow Condensed',sans-serif; font-size:.62rem; letter-spacing:.12em; text-transform:uppercase; color:rgba(255,255,255,.3); }
.svc-scope { padding:5rem; background:white; display:grid; grid-template-columns:1fr 1fr; gap:5rem; border-bottom:1px solid rgba(141,189,189,.15); }
.scope-list { list-style:none; }
.scope-list li { display:flex; gap:.75rem; padding:.75rem 0; border-bottom:1px solid rgba(141,189,189,.15); font-size:.88rem; font-weight:300; color:var(--mid); line-height:1.5; }
.scope-list li::before { content:'—'; color:var(--gold); flex-shrink:0; }
.svc-cta { background:var(--aqua-dark); padding:5rem; display:grid; grid-template-columns:1fr 1fr; gap:5rem; align-items:start; }
@media(max-width:900px) {
  .svc-hero-content { padding:0 2rem 2.5rem; }
  .svc-hero-content h1 { font-size:2.2rem; }
  .svc-overview, .svc-scope, .svc-cta { grid-template-columns:1fr; gap:2.5rem; padding:3rem 2rem; }
  .svc-images { padding:3rem 2rem; }
  .svc-img-grid { grid-template-columns:1fr 1fr; }
  .svc-img-grid .tall { grid-row:span 1; }
}
`;
